// Utils and Constants
import { calculatePercentage } from "../../common-utils/number-utils/NumberUtils";
import { toLocaleString } from "../../common-utils/string-utils/StringUtils";
import { getOtsTotal } from "../../utils/mavinMetrics";
import {
  GraphReportPieChartCityLimit,
  MetricKeys,
} from "./GraphReportsConstants";

// sum of metrics
function getMetricTotalOfCities(infoByCity = {}, metricKey) {
  const citiesMetricsArray = Object.values(infoByCity).map((cityInfo) => {
    const { otsTotal, tgOtsTotal } = getOtsTotal(cityInfo) || {};
    const { reachFrequency = {} } = cityInfo;
    const { reach, tgReach, frequency } = reachFrequency;

    switch (metricKey) {
      case MetricKeys.impressions:
        return otsTotal;
      case MetricKeys.tgImpressions:
        return tgOtsTotal;
      case MetricKeys.reach:
        return reach;
      case MetricKeys.tgReach:
        return tgReach;
      case MetricKeys.frequency:
        return frequency;
      default:
        return null;
    }
  });

  // return sum
  return citiesMetricsArray.reduce((acc, curr) => acc + curr, 0);
}

// construct PieChart Data Map
export function constructPieChartDataMap(campaign = {}) {
  const { infoByCity = {} } = campaign || {};

  // constants
  const { impressions, tgImpressions, reach, tgReach, frequency } = MetricKeys;

  // metrics totals
  const citiesTotalOts = getMetricTotalOfCities(infoByCity, impressions);
  const citiesTotalTgOts = getMetricTotalOfCities(infoByCity, tgImpressions);
  const citiesTotalReach = getMetricTotalOfCities(infoByCity, reach);
  const citiesTotalTgReach = getMetricTotalOfCities(infoByCity, tgReach);
  const citiesTotalFrequency = getMetricTotalOfCities(infoByCity, frequency);

  // impressions split
  const impressionsSplit = Object.values(infoByCity).map((cityInfo) => {
    const { cityName, otsTotal } = cityInfo;
    const otsPercentage = Number(calculatePercentage(otsTotal, citiesTotalOts));
    return {
      name: cityName,
      value: otsPercentage,
      count: toLocaleString(otsTotal),
    };
  });

  // tg impressions split
  const tgImpressionsSplit = Object.values(infoByCity).map((cityInfo) => {
    const { cityName, tgOtsTotal } = cityInfo;
    const tgOtsPercentage = Number(
      calculatePercentage(tgOtsTotal, citiesTotalTgOts)
    );
    return {
      name: cityName,
      value: tgOtsPercentage,
      count: toLocaleString(tgOtsTotal),
    };
  });

  // reach split
  const reachSplit = Object.values(infoByCity).map((cityInfo) => {
    const { cityName, reachFrequency = {} } = cityInfo;
    const { reach } = reachFrequency;
    const reachPercentage = Number(
      calculatePercentage(reach, citiesTotalReach)
    );
    return {
      name: cityName,
      value: reachPercentage,
      count: toLocaleString(reach),
    };
  });

  // tg reach split
  const tgReachSplit = Object.values(infoByCity).map((cityInfo) => {
    const { cityName, reachFrequency = {} } = cityInfo;
    const { tgReach } = reachFrequency;
    const tgReachPercentage = Number(
      calculatePercentage(tgReach, citiesTotalTgReach)
    );
    return {
      name: cityName,
      value: tgReachPercentage,
      count: toLocaleString(tgReach),
    };
  });

  // frequency split
  const frequencySplit = Object.values(infoByCity).map((cityInfo) => {
    const { cityName, reachFrequency = {} } = cityInfo;
    const { frequency } = reachFrequency;
    const frequencyPercentage = Number(
      calculatePercentage(frequency, citiesTotalFrequency)
    );
    return {
      name: cityName,
      value: frequencyPercentage,
      count: toLocaleString(frequency),
    };
  });

  // metrics map
  const metricsMap = {
    [MetricKeys.impressions]: impressionsSplit,
    [MetricKeys.tgImpressions]: tgImpressionsSplit,
    [MetricKeys.reach]: reachSplit,
    [MetricKeys.tgReach]: tgReachSplit,
    [MetricKeys.frequency]: frequencySplit,
  };

  return metricsMap;
}

// Helper: convert "1,64,42,052" → 16442052
function formatCount(str) {
  if (!str) {
    return 0;
  }

  return Number(str.replace(/,/g, ""));
}

// get Others Totals
function getOthersTotals(items = []) {
  let othersTotalValue = 0;
  let othersTotalCount = 0;

  for (const item of items) {
    othersTotalValue += item.value || 0;
    othersTotalCount += formatCount(item.count) || 0;
  }

  return { othersTotalValue, othersTotalCount };
}

// transform Metric Data
function transformMetricData(
  data = [],
  topDataCount = GraphReportPieChartCityLimit // constant
) {
  // if less then or equals topDataCount then don't need transform
  if (data && data.length <= topDataCount) {
    return {
      transformedMetricData: data,
      remainingData: [],
    };
  }

  // Sort by value descending
  const sorted = [...data].sort((a, b) => b.value - a.value);

  // topData and remaining
  const topData = sorted.slice(0, topDataCount);
  const remaining = sorted.slice(topDataCount);

  // others total
  const { othersTotalValue, othersTotalCount } = getOthersTotals(remaining);

  const others = {
    name: "Others",
    value: Number(othersTotalValue.toFixed(2)),
    count: toLocaleString(othersTotalCount),
  };

  const transformedMetricData = [...topData, others];

  return {
    transformedMetricData,
    remainingData: remaining,
  };
}

// transform TopData + Others data map
export function transformTopDataWithOthers(pieChartDataMap = {}) {
  const transformedDataMap = {};
  const remainingDataMap = {};

  for (const key in pieChartDataMap) {
    const metricData = pieChartDataMap[key];

    // transform data
    const { transformedMetricData, remainingData } =
      transformMetricData(metricData);

    // update
    transformedDataMap[key] = transformedMetricData;
    remainingDataMap[key] = remainingData;
  }

  return { transformedDataMap, remainingDataMap };
}

// construct city Impressions Split
export function constructCityImpressionsSplit(infoByCity) {
  const constructedImpressionsSplit = Object.values(infoByCity).map(
    (cityInfo) => {
      const { cityName } = cityInfo;
      const { otsTotal, tgOtsTotal } = getOtsTotal(cityInfo);

      return {
        name: cityName,
        Impressions: otsTotal,
        "TG Impressions": tgOtsTotal,
      };
    }
  );

  return constructedImpressionsSplit;
}

// construct city reach Split
export function constructCityReachSplit(summaryByCity) {
  const constructedReachSplit = Object.values(summaryByCity).map((cityInfo) => {
    const { cityName, reachFrequency } = cityInfo;
    const { reach, tgReach } = reachFrequency;
    return {
      name: cityName,
      Reach: reach,
      "TG Reach": tgReach,
    };
  });

  return constructedReachSplit;
}

// construct Cities List For Dropdown
export function constructCitiesListForDropdown(campaignCityWiseMedias) {
  const constructedCitiesList = Object.keys(campaignCityWiseMedias).map(
    (campaignCityId) => {
      const cityMediaList = campaignCityWiseMedias[campaignCityId];
      const { cityName } = cityMediaList[0];
      return {
        id: campaignCityId,
        label: cityName,
      };
    }
  );

  return constructedCitiesList;
}

// construct Media Site Wise genderRatio
export function constructMediaSiteWiseGenderRatio(
  campaignCityWiseMedias = {},
  selectedCityId = ""
) {
  // selected city media list
  const mediaListForSelectedCity = campaignCityWiseMedias[selectedCityId] || [];

  // data
  const constructedData = mediaListForSelectedCity.map((media) => {
    const { title, demographicStatistics = {} } = media;
    const { malePercent, femalePercent } = demographicStatistics || {};
    return {
      name: title,
      "Gender(%) Male": malePercent ? malePercent.toFixed(2) : 0,
      "Gender(%) Female": femalePercent ? femalePercent.toFixed(2) : 0,
    };
  });

  return constructedData;
}

// construct Media Site Wise Age Split
export function constructMediaSiteWiseAgeSplit(
  campaignCityWiseMedias = {},
  selectedCityId = ""
) {
  // selected city media list
  const mediaListForSelectedCity = campaignCityWiseMedias[selectedCityId] || [];

  // data
  const constructedData = mediaListForSelectedCity.map((media) => {
    const { title, demographicStatistics = {} } = media;
    const {
      age_0_4Percent,
      age_5_17Percent,
      age_18_22Percent,
      age_23_34Percent,
      age_35_44Percent,
      age_45_60Percent,
      age_60AbvPercent,
    } = demographicStatistics || {};

    // combining age splits for 45 and above
    const ageBelow18Percent = (age_0_4Percent || 0) + (age_5_17Percent || 0);
    const age45AndAbovePercent =
      (age_45_60Percent || 0) + (age_60AbvPercent || 0);

    return {
      name: title,
      "Age(%) Below 18": ageBelow18Percent ? ageBelow18Percent.toFixed(2) : 0,
      "Age(%) 18-22": age_18_22Percent ? age_18_22Percent.toFixed(2) : 0,
      "Age(%) 23-34": age_23_34Percent ? age_23_34Percent.toFixed(2) : 0,
      "Age(%) 35-44": age_35_44Percent ? age_35_44Percent.toFixed(2) : 0,
      "Age(%) 45-Above": age45AndAbovePercent
        ? age45AndAbovePercent.toFixed(2)
        : 0,
    };
  });

  return constructedData;
}

// check Any Value Present For Keys
export function checkAnyValuePresentForKeys(data = [], keysConfig = []) {
  // all dataKeys
  const keys = keysConfig.map((k) => k.dataKey);

  // check on data for each dataKey
  const isValuePresent = data.some((item) =>
    keys.some((key) => (item[key] || 0) > 0)
  );

  return isValuePresent;
}

// construct all cities medias
export function getAllCitiesMedias(cityWiseMedias = {}) {
  // all medias
  const allCitiesMedias = [];

  // adding all cities media into single array
  Object.keys(cityWiseMedias).map((cityId) => {
    const cityMediaList = cityWiseMedias[cityId];
    allCitiesMedias.push(...cityMediaList);
  });

  return allCitiesMedias;
}
